import { useRef, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Chip, IconButton, Button } from "@mui/material";
import useIsMobile from "components/hooks/useIsMobile";
import {
  EllipsisVertical,
  Phone,
  Check,
  X,
  PhoneMissed,
  PhoneOutgoing,
} from "lucide-react";
import { DelegationTaskComment } from "protogen/delegations_pb";
import { Attachment } from "protogen/common_pb";
import { UserAvatar } from "components/common/CurrentUserAvatar";
import { CurrentUserContext } from "components/context/RequireAuth";
import DateDisplay from "components/common/DateDisplay";
import { getStatusLabel } from "components/delegation/DelegationCard";
import PopperMenu, { createMenuOption } from "components/common/PopperMenu";
import AttachmentComp from "components/common/AttachmentComp";
import AttachmentDialog from "components/common/AttachmentDialog";
import SimpleRichEditor from "components/editor/SimpleRichEditor";
import { RichContent } from "components/editor/utils";
import DocumentViewer from "components/editor/DocumentViewer";

type Props = {
  comment: DelegationTaskComment;
  isLast?: boolean;
  onDelete?: (ref: string) => void;
  onUpdate?: (
    ref: string,
    content: string,
    attachments: any[],
    contentHtml?: string,
  ) => void;
  accountType?: "advisor" | "member";
};

export default ({
  comment,
  isLast,
  onDelete,
  onUpdate,
  accountType = "advisor",
}: Props) => {
  const currentUser = useContext(CurrentUserContext);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [menuOpen, setMenuOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [content, setContent] = useState<RichContent | null>(null);
  const [uploadsInProgress, setUploadsInProgress] = useState(false);
  const [openAttachment, setOpenAttachment] = useState<Attachment | null>(null);
  const anchorRef = useRef<HTMLButtonElement>(null);

  const canEdit = currentUser.ref === comment.author?.ref;
  const isPhoneCallOnly = comment.phoneCall && !comment.textContent;

  const getCallChip = (state: number) => {
    let icon: React.ReactNode;
    let label: string;
    let color: string;
    let bgColor: string;

    switch (state) {
      case 1: // Active/Outgoing
        icon = <PhoneOutgoing size={16} />;
        label = "Outgoing";
        color = "#C14743";
        bgColor = "#FFFFFF";
        break;
      case 2: // Completed
        icon = <Check size={16} />;
        label = "Completed";
        color = "#468E3E";
        bgColor = "#FFFFFF";
        break;
      case 3: // Missed
        icon = <PhoneMissed size={16} />;
        label = "Missed";
        color = "#9A6518";
        bgColor = "#FFFFFF";
        break;
      case 4: // Failed
        icon = <X size={16} />;
        label = "Failed";
        color = "#C14743";
        bgColor = "#FFFFFF";
        break;
      default:
        icon = <Phone size={16} />;
        label = "Call";
        color = "#8E9598";
        bgColor = "#FFFFFF";
    }

    return (
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
          padding: "4px 12px",
          borderRadius: "100px",
          border: "1px solid #D4D4D4",
          backgroundColor: bgColor,
        }}
      >
        <Box sx={{ display: "flex", color }}>{icon}</Box>
        <Typography
          variant="bodySmall"
          sx={{ color, fontWeight: 600, fontSize: "13px", lineHeight: 1 }}
        >
          {label}
        </Typography>
      </Box>
    );
  };

  const handleMenuToggle = () => {
    setMenuOpen((prev) => !prev);
  };

  const handleMenuClose = () => {
    setMenuOpen(false);
  };

  const handleSave = () => {
    if (onUpdate && content?.text) {
      onUpdate(
        comment.ref,
        content.text,
        content.attachments || [],
        content.html,
      );
      setIsEditing(false);
      setContent(null);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setContent(null);
  };

  if (isPhoneCallOnly) {
    const hasCallNotes = !!comment.callNotes?.content?.payload;

    return (
      <Box sx={{ display: "flex", gap: isMobile ? "8px" : "16px" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <UserAvatar
            user={comment.author}
            size={isMobile ? 26 : 34}
            clickable={!!comment.author?.isAdvisor}
          />
          <Box
            sx={(theme) => ({
              flexGrow: 1,
              borderLeft: `1px solid ${theme.palette.border}`,
              borderBottom: isLast
                ? `1px solid ${theme.palette.border}`
                : "none",
              paddingRight: isLast ? "20px" : "0",
              marginRight: isLast ? "-20px" : "0",
              marginBottom: isLast ? "20px" : "0",
              borderBottomLeftRadius: isLast ? "8px" : "0",
            })}
          />
        </Box>
        <Box
          sx={{
            flex: 1,
            border: hasCallNotes ? "1px solid #D4D4D4" : "none",
            borderRadius: "16px",
            padding: hasCallNotes ? "16px 24px" : "0",
            minHeight: hasCallNotes ? "auto" : "53px",
            marginBottom: hasCallNotes ? "8px" : "0",
            display: "flex",
            flexDirection: "column",
            gap: "12px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              paddingRight: hasCallNotes ? "0px" : "24px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  flexWrap: "wrap",
                }}
              >
                <Typography variant="bodyHeavy">
                  {comment.author?.displayName || "Unknown"}
                </Typography>
                <Typography variant="body">made a call</Typography>
                {comment.recipientType &&
                  comment.recipientType === "internal-advisor" && (
                    <Typography variant="body" color="text.secondary">
                      (advisors only)
                    </Typography>
                  )}
                <Box
                  onClick={
                    accountType === "member"
                      ? undefined
                      : () =>
                          navigate(
                            `/calls/${encodeURIComponent(
                              comment.phoneCall?.ref || "",
                            )}`,
                          )
                  }
                  sx={{
                    cursor: accountType === "member" ? "default" : "pointer",
                  }}
                >
                  {getCallChip(comment.phoneCall!.state)}
                </Box>
              </Box>
              {!isMobile &&
                comment.createdSec !== undefined &&
                comment.createdSec !== BigInt(0) && (
                  <DateDisplay
                    date={new Date(Number(comment.createdSec) * 1000)}
                  />
                )}
            </Box>
            {isMobile &&
              comment.createdSec !== undefined &&
              comment.createdSec !== BigInt(0) && (
                <DateDisplay
                  date={new Date(Number(comment.createdSec) * 1000)}
                />
              )}
          </Box>
          {hasCallNotes && (
            <Box
              sx={{
                padding: "12px",
                backgroundColor: "gray.2",
                borderRadius: "8px",
              }}
            >
              <Typography variant="bodySmall" sx={{ fontWeight: 600 }}>
                Notes:
              </Typography>
              <DocumentViewer
                content={JSON.parse(comment.callNotes.content.payload)}
                minHeight="0"
              />
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  const isStatusUpdateOnly =
    (comment.updatedStatus || comment.updatedAssignee || comment.unassigned) &&
    !comment.textContent &&
    !comment.phoneCall &&
    (!comment.attachments || comment.attachments.length === 0);

  return (
    <Box sx={{ display: "flex", gap: isMobile ? "8px" : "16px" }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: isStatusUpdateOnly ? "4px" : "8px",
        }}
      >
        {comment.author?.ref ? (
          <UserAvatar
            user={comment.author}
            size={isMobile ? 26 : 34}
            clickable={!!comment.author?.isAdvisor}
          />
        ) : comment.phoneCall ? (
          <Box
            sx={(theme) => ({
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: isMobile ? 26 : 34,
              height: isMobile ? 26 : 34,
              border: `1px solid ${theme.palette.border}`,
              borderRadius: "100px",
            })}
          >
            <Phone size={isMobile ? 14 : 18} color="#198282" />
          </Box>
        ) : null}
        <Box
          sx={(theme) => ({
            flexGrow: 1,
            borderLeft: `1px solid ${theme.palette.border}`,
            borderBottom: isLast ? `1px solid ${theme.palette.border}` : "none",
            paddingRight: isLast ? "20px" : "0",
            marginRight: isLast ? "-20px" : "0",
            marginBottom: isLast ? "20px" : "0",
            borderBottomLeftRadius: isLast ? "8px" : "0",
            minHeight: isStatusUpdateOnly ? "6px" : undefined,
          })}
        />
      </Box>
      <Box
        sx={{
          flex: 1,
          border: isStatusUpdateOnly ? "none" : "1px solid #D4D4D4",
          borderRadius: "16px",
          padding: isStatusUpdateOnly ? "0" : "16px 24px",
          marginBottom: isStatusUpdateOnly ? "0" : "12px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            marginTop: isStatusUpdateOnly ? "3px" : "0",
            paddingRight: isStatusUpdateOnly && !isMobile ? "24px" : "0",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                flexWrap: "wrap",
              }}
            >
              <Typography variant="bodyHeavy">
                {comment.author?.displayName || "Unknown"}
              </Typography>
              {comment.recipientType &&
                comment.recipientType === "internal-advisor" && (
                  <Typography variant="body" color="text.secondary">
                    (advisors only)
                  </Typography>
                )}
              {(comment.updatedStatus ||
                comment.updatedAssignee ||
                comment.unassigned) && (
                <Typography variant="body">updated this task to</Typography>
              )}
              {comment.updatedStatus &&
                !(comment.unassigned || comment.updatedAssignee) && (
                  <Chip
                    label={getStatusLabel(comment.updatedStatus)}
                    size="small"
                    sx={{
                      height: "24px",
                      fontSize: "12px",
                      backgroundColor:
                        comment.updatedStatus === "completed"
                          ? "#EAF2EA"
                          : comment.updatedStatus === "in_progress"
                            ? "#FBF2E5"
                            : "#F5F5F5",
                      color:
                        comment.updatedStatus === "completed"
                          ? "#468E3E"
                          : comment.updatedStatus === "in_progress"
                            ? "#9A6518"
                            : "#6B6E7B",
                    }}
                  />
                )}
              {comment.updatedAssignee && (
                <Chip
                  label="Claimed"
                  size="small"
                  sx={{
                    height: "24px",
                    fontSize: "12px",
                    backgroundColor: "#F5F5F5",
                    color: "#6B6E7B",
                  }}
                />
              )}
              {comment.unassigned && (
                <Chip
                  label="Re-queued"
                  size="small"
                  sx={{
                    height: "24px",
                    fontSize: "12px",
                    backgroundColor: "#FBF2E5",
                    color: "#9A6518",
                  }}
                />
              )}
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              {!isMobile &&
                comment.createdSec !== undefined &&
                comment.createdSec !== BigInt(0) && (
                  <DateDisplay
                    date={new Date(Number(comment.createdSec) * 1000)}
                  />
                )}
              {((onDelete && canEdit) || (onUpdate && canEdit)) &&
              !comment.phoneCall ? (
                <Box>
                  <IconButton
                    ref={anchorRef}
                    onClick={handleMenuToggle}
                    size="small"
                  >
                    <EllipsisVertical size={18} color="#8E9598" />
                  </IconButton>
                  <PopperMenu
                    open={menuOpen}
                    anchorRef={anchorRef}
                    handleClose={handleMenuClose}
                  >
                    {[
                      ...(onUpdate && canEdit
                        ? [
                            createMenuOption(
                              "Edit",
                              () => {
                                setIsEditing(true);
                                handleMenuClose();
                              },
                              "pencil",
                            ),
                          ]
                        : []),
                      ...(onDelete && canEdit
                        ? [
                            createMenuOption(
                              "Delete",
                              () => {
                                onDelete(comment.ref);
                                handleMenuClose();
                              },
                              "trash",
                            ),
                          ]
                        : []),
                    ]}
                  </PopperMenu>
                </Box>
              ) : (
                <Box sx={{ width: "28px", height: "28px" }} />
              )}
            </Box>
          </Box>
          {isMobile &&
            comment.createdSec !== undefined &&
            comment.createdSec !== BigInt(0) && (
              <DateDisplay date={new Date(Number(comment.createdSec) * 1000)} />
            )}
        </Box>
        {isEditing ? (
          <Box sx={{ marginTop: "12px" }}>
            <SimpleRichEditor
              forceOpen={true}
              disabled={uploadsInProgress}
              setContent={setContent}
              setUploadsInProgress={setUploadsInProgress}
              placeholder="Edit comment"
              initialContent={
                comment.contentType === "html"
                  ? comment.content
                  : comment.textContent || ""
              }
            />
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "8px",
                marginTop: "8px",
              }}
            >
              <Button variant="outlined" size="small" onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                size="small"
                onClick={handleSave}
                disabled={uploadsInProgress || !content?.text}
              >
                Save
              </Button>
            </Box>
          </Box>
        ) : (
          <>
            {comment.contentType === "html" && comment.content ? (
              <DocumentViewer content={comment.content} minHeight="0" />
            ) : comment.textContent ? (
              <Typography variant="body" sx={{ marginTop: "8px" }}>
                {comment.textContent}
              </Typography>
            ) : null}
            {comment.phoneCall && (
              <Box
                sx={{
                  marginTop: "8px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  flexWrap: "wrap",
                }}
              >
                <Box
                  onClick={
                    accountType === "member"
                      ? undefined
                      : () =>
                          navigate(
                            `/calls/${encodeURIComponent(
                              comment.phoneCall?.ref || "",
                            )}`,
                          )
                  }
                  sx={{
                    cursor: accountType === "member" ? "default" : "pointer",
                    display: "inline-block",
                  }}
                >
                  {getCallChip(comment.phoneCall.state)}
                </Box>
                {comment.recipientType &&
                  comment.recipientType === "internal-advisor" && (
                    <Typography variant="bodySmall" color="text.secondary">
                      (advisors only)
                    </Typography>
                  )}
                {comment.callNotes?.content?.payload && (
                  <Box
                    sx={{
                      marginTop: "12px",
                      padding: "12px",
                      backgroundColor: "#F5F5F5",
                      borderRadius: "8px",
                    }}
                  >
                    <Typography variant="bodySmall" sx={{ fontWeight: 600 }}>
                      Call Notes:
                    </Typography>
                    <DocumentViewer
                      content={JSON.parse(comment.callNotes.content.payload)}
                    />
                  </Box>
                )}
              </Box>
            )}
            {comment.attachments && comment.attachments.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                  marginTop: "12px",
                }}
              >
                {comment.attachments.map((attachment, index) => (
                  <AttachmentComp
                    key={index}
                    attachment={attachment}
                    openAttachment={setOpenAttachment}
                    sx={{
                      maxWidth: "120px",
                    }}
                  />
                ))}
              </Box>
            )}
          </>
        )}
      </Box>
      <AttachmentDialog
        attachment={openAttachment}
        onClose={() => setOpenAttachment(null)}
      />
    </Box>
  );
};
